import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  faArrowPointer,
  faLocationArrow,
  faRoute,
  faStreetView,
} from "@fortawesome/pro-solid-svg-icons";
import { faBucket } from "@fortawesome/pro-solid-svg-icons/faBucket";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { faSitemap } from "@fortawesome/pro-solid-svg-icons/faSitemap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import { EntityType, PermissionName } from "@mapmycustomers/shared";
import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import Located from "@mapmycustomers/shared/types/base/Located";
import {
  Activity,
  Company,
  EntitiesSupportedByEmailFeature,
  EntitySupportingCard,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";
import parsePhoneNumber from "@mapmycustomers/shared/util/phone/parsePhoneNumber";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util/ts";
import { addSchemeToUrlIfEmpty } from "@mapmycustomers/shared/util/url";
import { ModalFunctions } from "@mapmycustomers/ui";

import { ActionBarItemType } from "@app/component/ActionsBar";
import {
  createActivity,
  showGlobalCreateActivityModal,
} from "@app/component/createEditEntity/Activity/store/actions";
import EmailComposeModalsContext from "@app/component/email/creation/EmailComposeModalsContext";
import { useLocationUrl } from "@app/component/preview/useLocationUrl";
import { checkEmailSupporting } from "@app/store/email/actions";
import { hasPermission, isEmailServiceSupported } from "@app/store/iam";
import { isReadOnly } from "@app/store/iam/util";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import useClickTrackerContext from "@app/util/contexts/useClickTrackerContext";
import sendEmailViaClient from "@app/util/email/sendEmailViaClient";
import useEmailSendVariants from "@app/util/email/useEmailSendVariants";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { activityLayoutModel } from "@app/util/layout/impl";

import Actions from "./Actions";
import messages from "./messages";

interface Callbacks {
  onCreateActivity: typeof createActivity.request;
  onShowCreateActivityModal: typeof showGlobalCreateActivityModal;
}

const useCompanyPersonActions = (
  analyticService: AnalyticsService,
  activityTypes: ActivityType[],
  currentUser: User,
  entity: EntitySupportingCard,
  handleQuickActionButtonClick: (info: MenuInfo) => void,
  modal: Omit<ModalFunctions, "warn">,
  callbacks: Callbacks,
  onEmailSubscribe?: () => void,
  onEmailUnsubscribe?: () => void
) => {
  const intl = useIntl();
  const { onCreateActivity, onShowCreateActivityModal } = callbacks;
  const { onEmailModalShow } = useNonNullableContext(EmailComposeModalsContext);
  const clickTrackerContext = useClickTrackerContext();
  const fieldModel = getFieldModelByEntityType(entity.entity);
  const email = fieldModel.getByName(CompanyFieldName.EMAIL)?.getValueFor(entity) as string;
  const phone = parsePhoneNumber(
    (fieldModel.getByName(CompanyFieldName.PHONE)?.getValueFor(entity) as string) ?? undefined
  );
  const url = useLocationUrl(entity as Located);
  const dispatch = useDispatch();
  const emailServiceSupported = useSelector(isEmailServiceSupported);
  const isReadOnlyMember = isReadOnly(currentUser);

  const canUpdateCompany = useSelector(hasPermission)(PermissionName.COMPANY_UPDATE);
  const canUpdatePerson = useSelector(hasPermission)(PermissionName.PERSON_UPDATE);
  // Per-record access gates editing; fall back to the global permission when accessStatus is absent.
  const hasEditAccess =
    entity.accessStatus?.update ??
    (entity.entity === EntityType.COMPANY ? canUpdateCompany : canUpdatePerson);

  const canCreateActivity = useSelector(hasPermission)(PermissionName.ACTIVITY_CREATE);
  const canUpdateRoutes = useSelector(hasPermission)(PermissionName.ROUTES_UPDATE);
  const canSubscribe = useSelector(hasPermission)(PermissionName.UNSUBSCRIBED_EMAILS_DELETE);
  const canUnsubscribe = useSelector(hasPermission)(PermissionName.UNSUBSCRIBED_EMAILS_UPDATE);

  const emailSubscribed =
    entity.entity === EntityType.COMPANY || entity.entity === EntityType.PERSON
      ? entity.subscribed
      : undefined;

  const defaultActivityTypeVisibility = useMemo(
    () =>
      new Map(
        activityTypes.filter(({ isDefault }) => isDefault).map(({ active, name }) => [name, active])
      ),
    [activityTypes]
  );

  const relationshipActions = useMemo(() => {
    let children: ActionBarItemType[] = [];

    if (entity.entity === EntityType.COMPANY) {
      children = [
        {
          key: Actions.MANAGE_CHILD_COMPANIES,
          label: intl.formatMessage(messages.manageChildCompanies),
          onClick: handleQuickActionButtonClick,
        },
        {
          key: Actions.MANAGE_PARENT_COMPANY,
          label: intl.formatMessage(messages.manageParentCompany),
          onClick: handleQuickActionButtonClick,
        },
        {
          key: Actions.MANAGE_PEOPLE,
          label: intl.formatMessage(messages.managePeople),
          onClick: handleQuickActionButtonClick,
        },
      ];
    } else if (entity.entity === EntityType.PERSON) {
      children = [
        {
          disabled: entity.accounts ? entity.accounts.length >= 100 : undefined,
          key: Actions.MANAGE_COMPANIES,
          label: intl.formatMessage(messages.manageCompanies),
          onClick: handleQuickActionButtonClick,
        },
      ];
    }
    return children;
  }, [entity, intl, handleQuickActionButtonClick]);

  const activityTypeNames = useMemo(
    () => activityTypes.map((activityType) => activityType.name),
    [activityTypes]
  );

  const getActivity = useCallback(
    (
      activityName: string
    ): OnlyRequiredFields<Omit<Activity, "files">, "crmActivityType" | "entity"> => {
      const date = new Date();
      const activityType = activityTypes.find((activity) => activity.name === activityName);
      return {
        account: entity.entity === EntityType.COMPANY ? entity : undefined,
        assignee: currentUser,
        completed: true,
        contact: entity.entity === EntityType.PERSON ? entity : undefined,
        crmActivityType: activityType!,
        entity: EntityType.ACTIVITY,
        name: `${activityName} ${entity?.name ?? ""}`,
        startAt: date.toISOString(),
      };
    },
    [activityTypes, currentUser, entity]
  );

  const handleCall = useCallback(() => {
    clickTrackerContext.click(Actions.CALL);
    window.open(phone?.link ?? undefined, "_blank");
  }, [clickTrackerContext, phone?.link]);

  const handleGetDirections = useCallback(() => {
    clickTrackerContext.click(Actions.GET_DIRECTIONS);
    window.open(url ?? undefined, "_blank");
  }, [clickTrackerContext, url]);

  const handleLogActivity = useCallback(
    (activityName: string) => {
      if (activityName === DefaultActivityTypeName.CALL) {
        handleCall();
      }
      const activity = getActivity(activityName);
      // this shouldn't really happen, but still being cautious
      if (!activityTypeNames.includes(activityName)) {
        onShowCreateActivityModal({ activity });
        return;
      }

      const hasRequiredFields = activityLayoutModel.hasMissingRequiredFieldsFor(activity);
      if (hasRequiredFields) {
        onShowCreateActivityModal({ activity });
      } else {
        onCreateActivity({
          activity: { ...activity, assigneeId: activity.assignee?.id },
          customFieldsValues: [],
          layoutId: activityLayoutModel.defaultLayout.id,
        });
      }
    },
    [activityTypeNames, getActivity, handleCall, onCreateActivity, onShowCreateActivityModal]
  );

  const handleEmail = useCallback(() => {
    analyticService?.clicked(["Email from email client"], { email: email ?? "" });
    clickTrackerContext.click(Actions.EMAIL);
    sendEmailViaClient(
      email ?? "",
      intl,
      entity.name,
      defaultActivityTypeVisibility.get(DefaultActivityTypeName.EMAIL) ? modal : undefined,
      () => {
        handleLogActivity(DefaultActivityTypeName.EMAIL);
      }
    );
  }, [
    analyticService,
    clickTrackerContext,
    defaultActivityTypeVisibility,
    email,
    entity.name,
    handleLogActivity,
    intl,
    modal,
  ]);

  const handleVisitSite = useCallback(() => {
    clickTrackerContext.click(Actions.VISIT_SITE);
    const url = (entity as Company).website;
    if (url) {
      window.open(addSchemeToUrlIfEmpty(url), "_blank");
    }
  }, [clickTrackerContext, entity]);

  const sendViaMMCEmail = useCallback(() => {
    const recipient = entity as EntitiesSupportedByEmailFeature;

    analyticService?.clicked(["Email from MMC"]);
    dispatch(
      checkEmailSupporting({
        modal,
        successfulCallback: () => {
          onEmailModalShow?.({
            entities: [recipient],
          });
        },
      })
    );
  }, [analyticService, entity, modal, dispatch, onEmailModalShow]);

  const emailSendVariants: ActionBarItemType[] = useEmailSendVariants({
    email: email ?? "",
    onSendViaEmailClient: handleEmail,
    onSendViaMmcEmail: sendViaMMCEmail,
  });

  const emailChildrenElements = useMemo(() => {
    let result = emailSendVariants;
    const canManageSubscription = emailSubscribed ? canUnsubscribe : canSubscribe;
    if (emailServiceSupported && emailSubscribed !== undefined && canManageSubscription) {
      result = [
        ...emailSendVariants,
        emailSubscribed
          ? {
              key: Actions.EMAIL_UNSUBSCRIBE,
              label: intl.formatMessage(messages.emailUnsubscribe),
              onClick: onEmailUnsubscribe,
            }
          : {
              key: Actions.EMAIL_SUBSCRIBE,
              label: intl.formatMessage(messages.emailSubscribe),
              onClick: onEmailSubscribe,
            },
      ];
    }
    return result;
  }, [
    intl,
    canSubscribe,
    canUnsubscribe,
    emailServiceSupported,
    emailSendVariants,
    emailSubscribed,
    onEmailSubscribe,
    onEmailUnsubscribe,
  ]);

  const companyPersonActions = useMemo(() => {
    const actions: ActionBarItemType[] = [
      {
        disabled: !hasEditAccess,
        icon: <FontAwesomeIcon icon={faBucket} />,
        key: Actions.MANAGE_GROUPS,
        label: intl.formatMessage(messages.manageGroups),
        onClick: handleQuickActionButtonClick,
      },
      {
        disabled: !canUpdateRoutes,
        icon: <FontAwesomeIcon icon={faRoute} />,
        key: Actions.ADD_TO_ROUTE,
        label: intl.formatMessage(messages.addToRoute),
        onClick: handleQuickActionButtonClick,
      },
      {
        children: [
          ...relationshipActions,
          {
            key: Actions.ADD_DEAL,
            label: intl.formatMessage(messages.manageDeals),
            onClick: handleQuickActionButtonClick,
          },
        ],
        disabled: !hasEditAccess,
        icon: <FontAwesomeIcon icon={faSitemap} />,
        key: Actions.MANAGE_RELATIONSHIP,
        label: intl.formatMessage(messages.manageRelationships),
      },
      {
        disabled: !entity?.geoPoint,
        icon: <FontAwesomeIcon icon={faStreetView} />,
        key: Actions.STREET_VIEW,
        label: intl.formatMessage(messages.streetView),
        onClick: handleQuickActionButtonClick,
      },
      {
        children:
          !defaultActivityTypeVisibility.get(DefaultActivityTypeName.CALL) || !canCreateActivity
            ? undefined
            : [
                {
                  disabled: !phone?.link,
                  key: "callAndLogActivity",
                  label: intl.formatMessage(messages.logActivity),
                  onClick: () => handleLogActivity(DefaultActivityTypeName.CALL),
                },
                {
                  disabled: !phone?.link,
                  key: "simpleCall",
                  label: intl.formatMessage(messages.dontLogActivity),
                  onClick: handleCall,
                },
              ],
        disabled: !phone?.link,
        icon: <FontAwesomeIcon icon={faPhone} />,
        key: Actions.CALL,
        label: intl.formatMessage(messages.call),
        onClick:
          !defaultActivityTypeVisibility.get(DefaultActivityTypeName.CALL) || !canCreateActivity
            ? handleCall
            : undefined,
      },
      {
        disabled: !url,
        icon: <FontAwesomeIcon icon={faLocationArrow} />,
        key: Actions.GET_DIRECTIONS,
        label: intl.formatMessage(messages.getDirections),
        onClick: handleGetDirections,
      },
      {
        children: emailChildrenElements,
        disabled: !email || isReadOnlyMember,
        icon: <FontAwesomeIcon icon={faEnvelope} />,
        key: Actions.EMAIL,
        label: intl.formatMessage(messages.email),
      },
    ];
    if (entity.entity === EntityType.COMPANY) {
      actions.push({
        disabled: !entity.website,
        icon: <FontAwesomeIcon icon={faArrowPointer} />,
        key: Actions.VISIT_SITE,
        label: intl.formatMessage(messages.visitSite),
        onClick: handleVisitSite,
      });
    }
    return actions;
  }, [
    canCreateActivity,
    canUpdateRoutes,
    defaultActivityTypeVisibility,
    email,
    emailChildrenElements,
    entity,
    handleCall,
    handleGetDirections,
    handleLogActivity,
    handleQuickActionButtonClick,
    handleVisitSite,
    hasEditAccess,
    intl,
    phone,
    isReadOnlyMember,
    relationshipActions,
    url,
  ]);
  return companyPersonActions;
};

export default useCompanyPersonActions;
